import {
    graphqlFetch,
    GraphqlFetchOutput,
    GraphQLError
} from '@pylot-data/graphqlFetch'
import debounce from 'lodash.debounce'
import { useRouter } from 'next/router'
import { useCallback, useRef, useState } from 'react'
import {
    Cart,
    CartItemInterface,
    CurrencyEnum,
    Maybe
} from '@pylot-data/pylotschema'
import { updateCartItemsMutation } from './graphql/updateCartItemsMutation'
import { CartData, useCart } from './use-cart'
import { getIsSignedIn } from './utils/getIsSignedIn'
import { getGrandTotal } from './utils/optimisticAddToCart/getGrandTotal'
import { StockItem } from '@pylot-data/api/operations/graphql/stockValidationQuery'
import getStockValidation from '@pylot-data/api/operations/get-stock-validation'
import {
    SelectedConfigurableOption,
    BundleCartItem
} from '@pylot-data/pylotschema'

export type CartItemUpdateInput = {
    cart_item_uid: string
    quantity: number
    sku?: Maybe<string> | undefined
}

export type UpdateCartItemsInput = {
    input: {
        cart_id: string
        cart_items: CartItemUpdateInput[]
    }
    isSignedIn: boolean
}

export type UpdateCartItemsOutput = {
    updateCartItems: {
        cart: Cart
    }
    errors?: GraphQLError[]
}

export type StockAdjustment = {
    cartItemUid: string
    adjustedTo: number
    ts: number
}

type CartItemWithConfigurableOptions = CartItemInterface & {
    configurable_options?: SelectedConfigurableOption[]
}

type BundleStockItem = {
    sku: string
    requiredQty: number
}

type BundleCompositionMap = Map<string, BundleStockItem[]>

type MutationCartItemInput = {
    cart_item_uid: string
    quantity: number
}

const toMutationCartItems = (
    cartItems: CartItemUpdateInput[]
): MutationCartItemInput[] =>
    cartItems.map(({ cart_item_uid, quantity }) => ({
        cart_item_uid,
        quantity
    }))

const resolveStockSku = (
    item?: Maybe<CartItemInterface>
): string | undefined => {
    if (!item) return undefined

    const itemWithOptions = item as CartItemWithConfigurableOptions

    const skuFromConfigurable =
        itemWithOptions.configurable_options?.[0]?.sku_child

    if (skuFromConfigurable) {
        return skuFromConfigurable
    }

    return item.product?.sku ?? undefined
}

const isBundleCartItem = (
    item?: Maybe<CartItemInterface>
): item is BundleCartItem => item?.__typename === 'BundleCartItem'

const resolveBundleStockItems = (
    cartItem: BundleCartItem
): BundleStockItem[] => {
    const bundleOptions = cartItem.bundle_options
    const productItems = cartItem.product?.items

    if (!bundleOptions || !productItems) return []

    let skuCursor = 0

    const skus = productItems.flatMap(
        (item) =>
            item?.options?.map((o) => o?.product?.sku).filter(Boolean) ?? []
    )

    return bundleOptions.flatMap((option) => {
        if (!option?.values) return []

        return option.values.flatMap((value) => {
            if (!value?.quantity) return []

            const sku = skus[skuCursor++]
            if (!sku) return []

            return {
                sku,
                requiredQty: value.quantity
            }
        })
    })
}

const DEBOUNCE_TIME = 500

const getOptimisticData = (
    cartData: CartData,
    cartItems: CartItemUpdateInput[]
): CartData => {
    const items = [...(cartData.data.cart.items || [])]
    const updatedItems: Maybe<CartItemInterface>[] = []

    for (const item of items) {
        const correspondingItemToUpdate = cartItems.find(
            (updatedItem) => updatedItem.cart_item_uid === item?.uid!
        )

        if (!correspondingItemToUpdate) {
            updatedItems.push(item)
            continue
        }

        if (correspondingItemToUpdate.quantity === 0) {
            continue
        }

        const newItem = {
            ...item,
            quantity: correspondingItemToUpdate.quantity
        } as Maybe<CartItemInterface>

        if (item?.prices) {
            const currency =
                (item?.prices?.price.currency ||
                    item?.prices?.row_total?.currency) ??
                CurrencyEnum.Usd

            const price = Number(item?.prices?.price.value)
            const rowTotal = {
                value: price * correspondingItemToUpdate.quantity,
                currency
            }

            newItem!.prices = {
                ...item.prices,
                row_total: rowTotal,
                row_total_including_tax: rowTotal
            }
        }

        updatedItems.push(newItem)
    }

    const updatedGrandTotal = getGrandTotal(
        updatedItems,
        cartData.data.cart.prices?.grand_total
    )

    return {
        data: {
            cart: {
                ...cartData.data.cart,
                items: updatedItems,
                prices: {
                    ...cartData.data.cart.prices,
                    grand_total: updatedGrandTotal,
                    subtotal_excluding_tax: updatedGrandTotal
                }
            },
            isOptimistic: true
        }
    }
}

const formatData = (
    output: GraphqlFetchOutput<UpdateCartItemsOutput>
): CartData => ({
    data: {
        cart: output.data?.updateCartItems?.cart ?? null
    },
    errors: output.errors
})

const buildStockPayload = (
    cartItems: CartItemUpdateInput[],
    cartItemsFromCart: Maybe<CartItemInterface>[] | null | undefined
): {
    cartItemsWithSku: StockItem[]
    bundleCompositionMap: BundleCompositionMap
} => {
    const cartItemsWithSku: StockItem[] = []
    const bundleCompositionMap: BundleCompositionMap = new Map()

    cartItems.forEach((ci) => {
        const cartItem = cartItemsFromCart?.find(
            (item) => item?.uid === ci.cart_item_uid
        )

        if (isBundleCartItem(cartItem)) {
            const bundleItems = resolveBundleStockItems(cartItem)
            bundleCompositionMap.set(ci.cart_item_uid, bundleItems)

            bundleItems.forEach((bi) => {
                cartItemsWithSku.push({
                    sku: bi.sku,
                    quantity: String(ci.quantity * bi.requiredQty)
                })
            })
            return
        }

        const sku = resolveStockSku(cartItem)
        if (sku) {
            cartItemsWithSku.push({
                sku,
                quantity: ci.quantity.toString()
            })
        }
    })

    return { cartItemsWithSku, bundleCompositionMap }
}

export const useUpdateCartItems = () => {
    const { locale } = useRouter()
    const { data, mutate } = useCart()
    const [isUpdating, setIsUpdating] = useState(false)
    const [stockAdjustment, setStockAdjustment] = useState<
        StockAdjustment | undefined
    >(undefined)
    const pendingCartItemsRef = useRef<Map<string, CartItemUpdateInput>>(
        new Map()
    )
    const pendingLoadingCallbackRef = useRef<(() => void) | undefined>(
        undefined
    )

    const flushPendingUpdates = useCallback(
        debounce(async () => {
            const cartItems = Array.from(pendingCartItemsRef.current.values())
            pendingCartItemsRef.current.clear()

            if (!cartItems.length || !data?.data?.cart?.id) {
                pendingLoadingCallbackRef.current?.()
                pendingLoadingCallbackRef.current = undefined
                return
            }

            setIsUpdating(true)

            const { cartItemsWithSku, bundleCompositionMap } =
                buildStockPayload(cartItems, data.data.cart.items)

            try {
                const cartSnapshot = data

                const { getValidationStock } = await getStockValidation({
                    input: { items: cartItemsWithSku }
                })

                let stockCursor = 0

                const adjustedItems = cartItems.map((item) => {
                    const cartItem = data.data.cart.items?.find(
                        (ci) => ci?.uid === item.cart_item_uid
                    )

                    if (isBundleCartItem(cartItem)) {
                        const bundleItems =
                            bundleCompositionMap.get(item.cart_item_uid) ?? []

                        const maxQty = bundleItems.reduce((min, bi, i) => {
                            const stock = Number(
                                getValidationStock?.[stockCursor + i]?.stock ??
                                    0
                            )
                            return Math.min(
                                min,
                                Math.floor(stock / bi.requiredQty)
                            )
                        }, Infinity)

                        stockCursor += bundleItems.length

                        if (item.quantity > maxQty) {
                            setStockAdjustment({
                                cartItemUid: item.cart_item_uid,
                                adjustedTo: maxQty,
                                ts: Date.now()
                            })
                        }

                        return {
                            cart_item_uid: item.cart_item_uid,
                            quantity: Math.min(item.quantity, maxQty)
                        }
                    }

                    const stock = Number(
                        getValidationStock?.[stockCursor]?.stock ?? 0
                    )
                    stockCursor++

                    if (item.quantity > stock) {
                        setStockAdjustment({
                            cartItemUid: item.cart_item_uid,
                            adjustedTo: stock,
                            ts: Date.now()
                        })
                    }

                    return {
                        cart_item_uid: item.cart_item_uid,
                        quantity: Math.min(item.quantity, stock)
                    }
                })

                const optimisticData = getOptimisticData(
                    cartSnapshot,
                    adjustedItems
                )
                mutate(optimisticData, false)

                const response = await graphqlFetch<
                    UpdateCartItemsInput,
                    UpdateCartItemsOutput
                >({
                    query: updateCartItemsMutation,
                    variables: {
                        input: {
                            cart_id: String(data.data.cart.id),
                            cart_items: toMutationCartItems(adjustedItems)
                        },
                        isSignedIn: getIsSignedIn()
                    },
                    locale
                })

                if (response.data?.updateCartItems?.cart) {
                    await mutate(formatData(response), false)
                } else {
                    await mutate(cartSnapshot, false)
                }
            } catch (error) {
                console.error('Failed to update cart items', error)
                if (data) {
                    await mutate(data, false)
                }
            } finally {
                setIsUpdating(false)
                pendingLoadingCallbackRef.current?.()
                pendingLoadingCallbackRef.current = undefined
            }
        }, DEBOUNCE_TIME),
        [data, locale, mutate]
    )

    return {
        updateCartItems: useCallback(
            (cartItems: CartItemUpdateInput[], setIsLoading?: () => void) => {
                cartItems.forEach((ci) => {
                    pendingCartItemsRef.current.set(ci.cart_item_uid, {
                        cart_item_uid: ci.cart_item_uid,
                        quantity: ci.quantity
                    })
                })

                if (setIsLoading) {
                    pendingLoadingCallbackRef.current = setIsLoading
                }

                flushPendingUpdates()
            },
            [flushPendingUpdates]
        ),
        isUpdating,
        stockAdjustment
    }
}
